import { SourceType } from '@/feature/patients/types';
import type { DocumentEntry } from '../types';
import type { RawSourceMetadata } from './_shared';

export interface PdfSignedUrlCandidate {
  signedUrl?: string;
  contentType?: string;
  title?: string;
  uploadedAt?: string | Date;
}

interface PdfEntryFallback {
  title?: string;
  uploadedAt?: string | Date;
}

const getUrlPath = (signedUrl: string) => signedUrl.split(/[?#]/)[0] ?? '';

export const isPdfSignedUrlCandidate = ({
  signedUrl,
  contentType,
}: PdfSignedUrlCandidate): boolean => {
  const trimmedSignedUrl = signedUrl?.trim();
  if (!trimmedSignedUrl) return false;

  if (contentType?.toLowerCase().includes('pdf')) return true;

  return getUrlPath(trimmedSignedUrl).toLowerCase().endsWith('.pdf');
};

// For medical data documents, when source type is PROVIDER_FACILITY_RETRIEVAL,
// we need to check if the documentUrl is present in the sourceMetadata, except Vaccinations.
// Vaccinations will need to look up on signedUrl via sources[].
export const getProviderFacilityPdfEntry = (
  sourceType: SourceType,
  items: Array<{ sourceMetadata?: RawSourceMetadata; dateUploaded?: string }>,
  fallback: PdfEntryFallback = {},
): DocumentEntry | undefined => {
  // Only applies to Provider Facility Retrieval
  if (sourceType !== SourceType.PROVIDER_FACILITY_RETRIEVAL) return undefined;

  const match = items.find((item) => item.sourceMetadata?.documentUrl?.trim());
  if (!match) return undefined;

  return {
    kind: 'pdf',
    signedUrl: match.sourceMetadata!.documentUrl!.trim(),
    title: fallback.title,
    uploadedAt: match.dateUploaded ?? fallback.uploadedAt,
  };
};

export const buildSingleItemPdfEntry = <TItem>(
  items: readonly TItem[],
  getCandidates: (item: TItem) => PdfSignedUrlCandidate[],
  fallback: PdfEntryFallback = {},
): DocumentEntry | undefined => {
  if (items.length !== 1) return undefined;

  const pdfCandidates = getCandidates(items[0]).filter(isPdfSignedUrlCandidate);
  if (pdfCandidates.length !== 1) return undefined;

  const pdfCandidate = pdfCandidates[0];
  const signedUrl = pdfCandidate.signedUrl?.trim();
  if (!signedUrl) return undefined;

  return {
    kind: 'pdf',
    signedUrl,
    title: pdfCandidate.title ?? fallback.title,
    uploadedAt: pdfCandidate.uploadedAt ?? fallback.uploadedAt,
  };
};
